import { restoreExecutedSearch, resetSearchHistorySession } from '../../search-history/search-history.actions';
import { Action } from '@ngrx/store';
import { EMPTY as noFavoriteRequest, finalize } from 'rxjs';
import { groupBy, mergeMap as mergeSearchModes } from 'rxjs';
import { ensureRealEstateList } from '../actions/real-estate.actions';
import { filter } from 'rxjs';
import { clearRealEstatePolygon } from '../actions/real-estate.actions';
import { buildSearchRequest, searchErrorKey } from '../../models/search-request';
import {
  setRealEstatePolygon,
  resetRealEstateSearch,
  retryRealEstateSearch,
  setRealEstateMapView,
} from '../actions/real-estate.actions';
import { logout, clearAuthUser, clearSessionState } from '../actions/auth.actions';
import { EMPTY, takeUntil } from 'rxjs';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApiService } from '../../services/api.service';
import { catchError, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';
import {
  addRealEstate,
  addRealEstateFailure,
  addRealEstateReport,
  addRealEstateReportFailure,
  addRealEstateReportSuccess,
  addRealEstateSuccess,
  changeRealEstatePage,
  changeRealEstatePageSize,
  changeRealEstateSort,
  getRealEstate,
  getRealEstateFailure,
  getRealEstateSuccess,
  getTopRealEstates,
  getTopRealEstatesSuccess,
  likeRealEstate,
  likeRealEstateFailure,
  likeRealEstateSuccess,
  removeRealEstate,
  removeRealEstateFailure,
  removeRealEstateSuccess,
  searchMapRealEstates,
  searchMapRealEstatesFailure,
  searchMapRealEstatesSuccess,
  searchRealEstateReports,
  searchRealEstateReportsFailure,
  searchRealEstateReportsSuccess,
  searchRealEstates,
  searchRealEstatesFailure,
  searchRealEstatesSuccess,
  setRealEstateSold,
  setRealEstateSoldFailure,
  setRealEstateSoldSuccess,
  updateRealEstate,
  updateRealEstateFailure,
  updateRealEstateReport,
  updateRealEstateReportFailure,
  updateRealEstateReportSuccess,
  updateRealEstateSuccess,
  upgradeRealEstate,
} from '../actions/real-estate.actions';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { clearUser, getUser } from '../actions/users.action';
import { Store } from '@ngrx/store';
import { selectRealEstateState } from '../selectors/real-estate.selectors';

@Injectable()
export class RealEstateSearchEffects {
  constructor(
    private actions$: Actions,
    private api: ApiService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private store: Store
  ) {}

  initializeListOnTab$ = createEffect(() => this.actions$.pipe(
    ofType(setRealEstateMapView), filter(action => !action.isRealEstateMapView),
    withLatestFrom(this.store.select(selectRealEstateState)),
    filter(([,state]) => !state.propertyResults && !state.listLoading),
    map(([,state]) => searchRealEstates({ query: (state.query ?? state.listDraft ?? {}) as any }))
  ));

  searchRealEstates$ = createEffect(() => this.actions$.pipe(
    ofType(restoreExecutedSearch, searchRealEstates, searchMapRealEstates, setRealEstatePolygon, retryRealEstateSearch, ensureRealEstateList, changeRealEstateSort, changeRealEstatePage, changeRealEstatePageSize),
    filter(action => action.type !== restoreExecutedSearch.type || action.domain === 'REAL_ESTATE'),
    withLatestFrom(this.store.select(selectRealEstateState)),
    filter(([action,state]) => action.type === ensureRealEstateList.type ? !state.historyRestored && !state.isRealEstateMapView && !state.propertyResults :
      !([changeRealEstateSort.type, changeRealEstatePage.type, changeRealEstatePageSize.type] as string[]).includes(action.type) || !state.isRealEstateMapView),
    map(([action,state]) => ({action, state, isMap: (action.type === restoreExecutedSearch.type && action.mode === 'map') || action.type === searchMapRealEstates.type || action.type === setRealEstatePolygon.type || (action.type === retryRealEstateSearch.type && state.isRealEstateMapView)})),
    // One switchMap per mode: supersede only requests belonging to the same result set.
    groupBy(request => request.isMap),
    mergeSearchModes(requests => requests.pipe(switchMap(({state,isMap}) =>
      (isMap
        ? this.api.searchMapProperties(buildSearchRequest(state, true)).pipe(map((results): Action => searchMapRealEstatesSuccess({propertyMapResults: results})))
        : this.api.searchProperties(buildSearchRequest(state, false)).pipe(map((results): Action => searchRealEstatesSuccess({propertyResults: results})))
      ).pipe(
        takeUntil(this.actions$.pipe(ofType(logout, clearAuthUser, clearSessionState, clearRealEstatePolygon, resetRealEstateSearch),
          withLatestFrom(this.store.select(selectRealEstateState)),
          filter(([action,current]) => action.type === resetRealEstateSearch.type ? current.isRealEstateMapView === isMap : action.type !== clearRealEstatePolygon.type || isMap))),
        takeUntil(this.actions$.pipe(ofType(resetSearchHistorySession, restoreExecutedSearch), filter(action => action.type === resetSearchHistorySession.type || action.domain === 'REAL_ESTATE'))),
        catchError(error => of(isMap ? searchMapRealEstatesFailure({error: searchErrorKey(error)}) : searchRealEstatesFailure({error: searchErrorKey(error)})))
      )
    )))
  ));

  searchRealEstateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(searchRealEstatesFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant(error));
        })
      ),
    { dispatch: false }
  );

  searchMapRealEstatesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(searchMapRealEstatesFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant(error));
        })
      ),
    { dispatch: false }
  );

  getTopRealEstates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTopRealEstates),
      mergeMap(() =>
        this.api.getTopRealEstates().pipe(
          map((topPropertyResults) =>
            getTopRealEstatesSuccess({ topPropertyResults: topPropertyResults })
          ),
          catchError((error) => of(getRealEstateFailure({ error: error.message })))
        )
      )
    )
  );

  getTopRealEstatesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getRealEstateFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant(error));
        })
      ),
    { dispatch: false }
  );

  getRealEstate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getRealEstate),
      mergeMap(({ realEstateId }) =>
        this.api.getRealEstateById(realEstateId).pipe(
          map((fetchedRealEstate) => getRealEstateSuccess({ realEstate: fetchedRealEstate })),
          catchError((error) => of(getRealEstateFailure({ error: error.message })))
        )
      )
    )
  );

  getRealEstateSuccess$ = createEffect(() => this.actions$.pipe(ofType(getRealEstateSuccess)), {
    dispatch: false,
  });

  getRealEstateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getRealEstateFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('PROPERTY_GET_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  addRealEstate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addRealEstate),
      mergeMap(({ formData }) =>
        this.api.addNewRealEstate(formData).pipe(
          map((savedRealEstate) => addRealEstateSuccess({ realEstate: savedRealEstate })),
          catchError((error) => of(addRealEstateFailure({ error: error.message })))
        )
      )
    )
  );

  addRealEstateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addRealEstateSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('PROPERTY_ADD_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  addRealEstateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addRealEstateFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('PROPERTY_ADD_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  updateRealEstate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateRealEstate),
      mergeMap(({ realEstateId, formData }) =>
        this.api.editRealEstate(formData, realEstateId).pipe(
          map((updatedRealEstate) => updateRealEstateSuccess({ realEstate: updatedRealEstate })),
          catchError((error) => of(updateRealEstateFailure({ error })))
        )
      )
    )
  );

  upgradeRealEstate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(upgradeRealEstate),
      mergeMap(({ realEstateId }) =>
        this.api.upgradeRealEstate(realEstateId).pipe(
          map((updatedRealEstate) => updateRealEstateSuccess({ realEstate: updatedRealEstate })),
          catchError((error) => of(updateRealEstateFailure({ error })))
        )
      )
    )
  );

  updateRealEstateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateRealEstateSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('PROPERTY_UPDATE_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  updateRealEstateSuccessLoadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateRealEstateSuccess),
      map((action) => {
        clearUser();
        return getUser({ userId: action.realEstate.user.id });
      })
    )
  );

  updateRealEstateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateRealEstateFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('PROPERTY_UPDATE_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  private readonly pendingFavorites = new Set<string>();

  likeRealEstate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(likeRealEstate),
      mergeMap(({ userId, realEstateId, isLike }) => {
        const key = userId + ':' + realEstateId;
        if (this.pendingFavorites.has(key)) return noFavoriteRequest;
        this.pendingFavorites.add(key);
        return this.api.likeRealEstate(userId, realEstateId, isLike).pipe(
          finalize(() => this.pendingFavorites.delete(key)),
          map((updatedRealEstate) => likeRealEstateSuccess({ realEstate: updatedRealEstate, userId })),
          catchError((error) => of(likeRealEstateFailure({ error })))
        );
      })
    )
  );

  likeRealEstateSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(likeRealEstateSuccess),
        tap((action) => {
          this.toastr.success(
            action.realEstate.liked
              ? this.translate.instant('PROPERTY_LIKE_SUCCESS')
              : this.translate.instant('PROPERTY_UNLIKE_SUCCESS')
          );
        })
      ),
    { dispatch: false }
  );

  likeRealEstateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(likeRealEstateFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('PROPERTY_LIKE_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  setRealEstateSold$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setRealEstateSold),
      mergeMap(({ realEstateId }) =>
        this.api.setRealEstateSold(realEstateId).pipe(
          map((updatedRealEstate) => setRealEstateSoldSuccess({ realEstate: updatedRealEstate })),
          catchError((error) => of(setRealEstateSoldFailure({ error })))
        )
      )
    )
  );

  setRealEstateSoldSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setRealEstateSoldSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('PROPERTY_UPDATE_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  setRealEstateSoldFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setRealEstateSoldFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('PROPERTY_UPDATE_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  removeRealEstate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeRealEstate),
      mergeMap(({ id }) =>
        this.api.deleteRealEstate(id).pipe(
          map(() => removeRealEstateSuccess({ id })),
          catchError((error) => of(removeRealEstateFailure({ error: error.message })))
        )
      )
    )
  );

  removeRealEstateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(removeRealEstateSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('PROPERTY_DELETE_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  removeRealEstateFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(removeRealEstateFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('PROPERTY_DELETE_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  searchRealEstateReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(searchRealEstateReports),
      mergeMap(({ reportQuery }) =>
        this.api.searchRealEstateReports(reportQuery).pipe(
          map((results) => searchRealEstateReportsSuccess({ realEstateReports: results })),
          catchError((error) => of(searchRealEstateReportsFailure({ error: error.message })))
        )
      )
    )
  );

  searchRealEstateReportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(searchRealEstateReportsFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('PROPERTY_REPORT_SEARCH_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  addRealEstateReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addRealEstateReport),
      mergeMap(({ entity, id, model }) =>
        this.api.createReport(entity, id, model).pipe(
          map((savedReport) => addRealEstateReportSuccess({ report: savedReport })),
          catchError((error) => of(addRealEstateReportFailure({ error: error.message })))
        )
      )
    )
  );

  addRealEstateReportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addRealEstateReportSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('REPORT_ADD_SUCCESS'));
        })
      ),
    { dispatch: false }
  );

  addRealEstateReportFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addRealEstateReportFailure),
        tap(({ error }) => {
          this.toastr.error(this.translate.instant('REPORT_ADD_FAIL'), error);
        })
      ),
    { dispatch: false }
  );

  updateRealEstateReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateRealEstateReport),
      mergeMap(({ reportId, status }) =>
        this.api.updateRealEstateReport(reportId, status).pipe(
          map((updatedRealEstateReport) =>
            updateRealEstateReportSuccess({ report: updatedRealEstateReport })
          ),
          catchError((error) => of(updateRealEstateReportFailure({ error })))
        )
      )
    )
  );

  updateRealEstateReportSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateRealEstateReportSuccess),
        tap(() => {
          this.toastr.success(this.translate.instant('REPORT_UPDATE_SUCCESS'));
        })
      ),
    { dispatch: false }
  );
}
